import { isFlightItem } from 'checkout/lib/utils/flights/cart'
import { getCheckoutFlightV2SearchUrl, getCheckoutFlightV1SearchUrl, getStandaloneFlightsV1SearchUrl, getStandaloneFlightsV2SearchUrl } from 'checkout/lib/utils/flights/links'
import { nonNullable } from 'lib/array/arrayUtils'
import {
  buildFlightItemBreakdownView,
  formatFareType,
  formatFlightPassengersShorthand,
  formatJourneyShorthand,
} from 'lib/checkout/flights/format'
import createSelector from 'lib/web/createSelector'

import { getJourneyV2IdKey } from 'lib/flights/flightUtils'
import { convertJourneyV2ToJourneyFlight } from 'api/lib/convertJourneyV2'

export const getFlightItems = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items): Array<App.Checkout.FlightItem> => items.filter(isFlightItem),

)

export const getBundledFlightItems = createSelector(
  getFlightItems,
  (items): Array<App.Checkout.FlightItem> => items.filter(item => !!item.bundledItemIds?.length),

)

export const cartIncludesFlights = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items): boolean => items.some(isFlightItem),
)

function mapFlightV2ToView(
  item: App.Checkout.FlightItem,
  departing?: App.JourneyV2,
  returning?: App.JourneyV2,
): App.Checkout.FlightItemView {
  const departingFare = departing?.price.all.totalFare ?? 0
  const returningFare = returning?.price.all.totalFare ?? 0
  const departingSurcharge = departing?.price.all.totalFees ?? 0
  const returningSurcharge = returning?.price.all.totalFees ?? 0
  const isBundled = (item.bundledItemIds?.length ?? 0) > 0

  return {
    itemId: item.itemId,
    item,
    journeyId: item.searchId,
    provider: departing?.provider,
    passengers: item.passengers,
    departing: (item.departing && departing) ? {
      journeyFlight: convertJourneyV2ToJourneyFlight(departing),
      extras: item.departing.extras,
      cost: item.departing.cost,
      fareFamily: item.departing.fareFamily,
    } : undefined,
    returning: (item.returning && returning) ? {
      journeyFlight: convertJourneyV2ToJourneyFlight(returning),
      extras: item.returning.extras,
      cost: item.returning.cost,
      fareFamily: item.returning.fareFamily,
    } : undefined,
    designation: 'Flights',
    validatingCarrier: departing?.carrier,
    validatingCarrierName: departing?.carrierName ?? '',
    validatingCarrierLogo: departing?.carrierLogo ?? '',
    fareType: item.fareType,
    price: item.totalFare ?? (departingFare + returningFare),
    value: 0, // No data on this
    surcharge: departingSurcharge + returningSurcharge,
    seatSelectionPolicy: departing?.flightGroup.flights[0].seatSummary.selectionPolicy,
    isBundled,
    taxesAndFees: 0,
    quotedFare: item.quotedFare,
    otherFees: item.otherFees,
    searchFlightsUrl: isBundled ?
      getCheckoutFlightV2SearchUrl(departing, returning) :
      getStandaloneFlightsV2SearchUrl(item, departing, returning),
  }
}

function mapFlightToView(
  item: App.Checkout.FlightItem,
  journey?: App.Journey,
): App.Checkout.FlightItemView {
  const isBundled = (item.bundledItemIds?.length ?? 0) > 0

  return {
    itemId: item.itemId,
    item,
    journeyId: item.searchId,
    provider: journey?.provider,
    passengers: item.passengers,
    departing: (item.departing && journey?.departing) ? {
      journeyFlight: journey.departing,
      extras: item.departing.extras,
      cost: item.departing.cost,
      fareFamily: item.departing.fareFamily,
    } : undefined,
    returning: (item.returning && journey?.returning) ? {
      journeyFlight: journey.returning,
      extras: item.returning.extras,
      cost: item.returning.cost,
      fareFamily: item.returning.fareFamily,
    } : undefined,
    designation: 'Flights',
    validatingCarrier: journey?.validatingCarrier,
    validatingCarrierName: journey?.validatingCarrierName ?? '',
    validatingCarrierLogo: journey?.validatingCarrierLogo ?? '',
    fareType: item.fareType,
    price: (item.totalFare || journey?.cost) ?? 0,
    value: 0, // No data on this
    surcharge: journey?.fee,
    seatSelectionPolicy: journey?.seatSelectionPolicy,
    isBundled,
    taxesAndFees: 0,
    otherFees: item.otherFees,
    quotedFare: item.quotedFare,
    searchFlightsUrl: isBundled ?
      getCheckoutFlightV1SearchUrl(journey) :
      getStandaloneFlightsV1SearchUrl(item, journey),
  }
}

export const getFlightItemsView = createSelector(
  (state: App.State) => getFlightItems(state),
  (state: App.State) => state.flights.journeysById,
  (flightItems, journeys): App.WithDataStatus<Array<App.Checkout.FlightItemView>> => {
    const anyItemsFetching = flightItems.some(item => item.fetchingFare)
    const flightViews = nonNullable(
      flightItems.map<App.Checkout.FlightItemView | undefined>(item => {
        if (item.searchId) {
          if (item.journeyType === 'journeyV2') {
            const departingKey = getJourneyV2IdKey(item.departing?.journeyId!, item.searchId, item.departing?.fareFamily?.id)
            const returningKey = getJourneyV2IdKey(item.returning?.journeyId!, item.searchId, item.returning?.fareFamily?.id)

            return mapFlightV2ToView(
              item,
              journeys[departingKey] as App.JourneyV2,
              journeys[returningKey] as App.JourneyV2,

            )
          }
          return mapFlightToView(item, journeys[item.searchId] as App.Journey)
        }
        return mapFlightToView(item)
      }),
    )

    return {
      hasRequiredData: !anyItemsFetching && flightViews.length === flightItems.length,
      data: flightViews,
    }
  },
)

export const getFlightBreakdownView = createSelector(
  getFlightItemsView,
  (flightViewsWithStatus): App.WithDataStatus<Array<App.Checkout.PriceBreakdownView>> => {
    const flightBreakdownViews = flightViewsWithStatus.data.map<App.Checkout.PriceBreakdownView>(view => {
      const supplementaryData = {
        validatingCarrierName: view.validatingCarrierName,
        validatingCarrierLogo: view.validatingCarrierLogo,
      }
      const breakdownView = {
        title: formatFareType(view.fareType),
        additionalInfoText: [
          ...(view.departing ? [formatJourneyShorthand(view.departing.journeyFlight)] : []),
          formatFlightPassengersShorthand(view.passengers),
        ],
        price: view.isBundled ? 0 : (view.price ?? 0),
        items: [
          ...(view.departing ? [buildFlightItemBreakdownView(view, view.departing, supplementaryData)] : []),
          ...(view.returning ? [buildFlightItemBreakdownView(view, view.returning, supplementaryData)] : []),
        ],
      }
      return breakdownView
    })

    return {
      hasRequiredData: flightViewsWithStatus.hasRequiredData,
      data: flightBreakdownViews,
    }
  },
)
